import React from "react";
import SiteHeader from "../components/SiteHeader";
import AppPreview from "../components/AppPreview";
import Reveal from "../components/Reveal";
import {
  IconArrowRight,
  IconBolt,
  IconChart,
  IconChat,
  IconErrand,
  IconFood,
  IconGrocery,
  IconPin,
  IconReceipt,
  IconRide,
  IconSparkle,
  IconStore,
  IconWallet,
} from "../components/Icons";
import { SHOW_MERCHANT_PLANS, SHOW_PRICING, SIGNUP_URL } from "../siteConfig";
import { useDocumentMeta } from "../useDocumentMeta";
import "../styles/home.css";

const SERVICES = [
  {
    key: "food",
    tone: "amber",
    tag: "Meals & drinks",
    icon: <IconFood size={22} />,
    title: "Foods",
    body: "Order meals and drinks from local restaurants and cafés with live order tracking.",
    points: ["Restaurants and cafés", "Order now or schedule", "Cash or GCash"],
  },
  {
    key: "grocery",
    tone: "teal",
    tag: "Shopping",
    icon: <IconGrocery size={22} />,
    title: "Pabili",
    body: "Shop groceries, pharmacy items, and everyday essentials from nearby stores.",
    points: ["Groceries and pharmacy", "Store receipt in your booking", "Cash or GCash"],
  },
  {
    key: "pasugo",
    tone: "violet",
    tag: "Errands",
    icon: <IconErrand size={22} />,
    title: "Pasugo",
    body: "Tell a rider what you need. Name the shop and drop-off, or leave them to the rider.",
    points: ["Buy something for you", "Send an item across town", "Cash or GCash"],
  },
  {
    key: "angkas",
    tone: "rose",
    tag: "Rides",
    icon: <IconRide size={22} />,
    title: "Angkas",
    body: "Book a rider to take you across town. Add your destination to see the fare before you book.",
    points: ["Free helmet and hair net", "One passenger per ride", "Cash or GCash"],
  },
] as const;

/* Fare schedule per the current published Rider Agreement (Sections 6.2, 6.4).
   Flag-down differs by service; the per-kilometre rate is shared, and the
   first kilometre is free of the per-km charge. There are deliberately no
   surcharges — no night, hazard, long-distance, or peak-hour add-ons. */
const PRICING = [
  {
    label: "Flag-down — Pasugo",
    amount: "₱50",
    unit: "",
    icon: <IconErrand size={20} />,
    desc: "Starting rate for a delivery or Pasugo errand, covering the rider's start-of-trip cost.",
  },
  {
    label: "Flag-down — Angkas",
    amount: "₱40",
    unit: "",
    icon: <IconRide size={20} />,
    desc: "Starting rate for a passenger ride.",
  },
  {
    label: "Per kilometer",
    amount: "₱15",
    unit: "/km",
    icon: <IconPin size={20} />,
    desc: "The first kilometre is free of the per-km rate. Trips of 1 km or less pay the flag-down rate only.",
  },
  {
    label: "Booking fee",
    amount: "₱5",
    unit: " / 3%",
    icon: <IconReceipt size={20} />,
    desc: "₱5 per ride, or 3% of the order total capped at ₱30 for deliveries and Pasugo. It funds the platform and never reduces the rider's fare.",
  },
];

/* Matches the app's checkout: Cash on Delivery or GCash (paid to PABS's GCash
   account, receipt uploaded and verified in the app). No cards. */
const PAYMENTS = [
  {
    title: "Cash on Delivery",
    body: "Pay your rider when your order arrives, your errand is done, or your ride ends.",
  },
  {
    title: "GCash",
    body: "Send the payment to the PABS GCash account shown in your booking and upload the receipt. We confirm it before the rider arrives.",
  },
  {
    title: "You see the fare first",
    body: "The fare, or the starting fare when there's no end point yet, is shown before you book. Store items follow the store receipt.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Pick your service",
    body: "Choose Foods, Pabili, Pasugo, or Angkas — each has its own flow built for the job.",
  },
  {
    n: "02",
    title: "Add the details",
    body: "Fill your cart, or say what you need and where. See the fare before you book.",
  },
  {
    n: "03",
    title: "Track it in Bookings",
    body: "Follow your rider on the map, from Rider assigned to Order delivered, and chat or call if you need to.",
  },
];

const HomePage: React.FC = () => {
  useDocumentMeta({
    title: "PABS — Food, grocery, errands, and rides",
    description:
      "PABS (Pickup And Bring Stuff) is an all-in-one platform for food and drinks delivery, grocery and pharmacy delivery, Pasugo errands, and Angkas rides in the Philippines.",
    canonicalPath: "/",
    ogImage: "https://pabs.online/logo512.png",
    jsonLdId: "pabs-home-jsonld",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "PABS",
        alternateName: "Pickup And Bring Stuff",
        url: "https://pabs.online/",
        logo: "https://pabs.online/logo512.png",
        sameAs: [],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "PABS",
        url: "https://pabs.online/",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://pabs.online/faq?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "On-demand delivery, errands, and rides",
        provider: { "@type": "Organization", name: "PABS" },
        areaServed: { "@type": "Country", name: "Philippines" },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "PABS services",
          itemListElement: [
            {
              "@type": "Offer",
              name: "Foods",
              description: "Food and beverage delivery from restaurants and cafés.",
            },
            {
              "@type": "Offer",
              name: "Pabili",
              description: "Grocery, pharmacy, and essentials delivery from nearby stores.",
            },
            {
              "@type": "Offer",
              name: "Pasugo",
              description: "On-demand errand service — buy-and-deliver or deliver-my-item.",
            },
            {
              "@type": "Offer",
              name: "Angkas",
              description: "Motorcycle passenger rides across town, paid in cash on arrival.",
            },
          ],
        },
      },
    ],
  });

  return (
    <>
      <SiteHeader current="/" />

      <main id="main">
        {/* ================= Hero ================= */}
        <section className="hero">
          <div className="hero__bg" aria-hidden="true">
            <span className="hero__orb hero__orb--1" />
            <span className="hero__orb hero__orb--2" />
            <span className="hero__grid" />
          </div>

          <div className="u-container hero__inner">
            <div className="hero__copy">
              <Reveal>
                <span className="badge hero__badge">
                  <IconSparkle size={13} />
                  Now live · Angkas
                </span>
              </Reveal>

              <Reveal delay={60}>
                <h1 className="t-display hero__title">
                  Deliveries and errands,
                  <br />
                  <span className="t-grad">brought to you.</span>
                </h1>
              </Reveal>

              <Reveal delay={120}>
                <p className="t-lead hero__lead">
                  Order food, shop groceries and essentials with Pabili, send a
                  rider on a Pasugo errand, or book an Angkas ride.
                  Transparent fares, clear pickup and drop-off details, and
                  reliable status updates.
                </p>
              </Reveal>

              <Reveal delay={180}>
                <div className="btn-row hero__ctas">
                  <a className="btn btn--primary btn--lg" href={SIGNUP_URL}>
                    Create account
                    <IconArrowRight size={18} className="btn__icon btn__icon--arrow" />
                  </a>
                  <a className="btn btn--secondary btn--lg" href="#services">
                    See services
                  </a>
                </div>
              </Reveal>

              <Reveal delay={240}>
                <p className="hero__meaning">
                  <strong>PABS</strong> — <em>Pickup And Bring Stuff</em>.
                </p>
              </Reveal>
            </div>

            <Reveal delay={140} className="hero__art">
              <AppPreview />
            </Reveal>
          </div>

          {/* Marquee of what PABS actually offers — factual, no invented stats */}
          <div className="u-container">
            <Reveal delay={300}>
              <ul className="hero__facts">
                <li>
                  <IconBolt size={17} />
                  Deliveries, errands, and rides
                </li>
                <li>
                  <IconReceipt size={17} />
                  Transparent fares
                </li>
                <li>
                  <IconWallet size={17} />
                  Cash or GCash
                </li>
                <li>
                  <IconPin size={17} />
                  Serving the Philippines
                </li>
              </ul>
            </Reveal>
          </div>
        </section>

        {/* ================= Services ================= */}
        <section className="u-section" id="services">
          <div className="u-container">
            <Reveal>
              <div className="sec-head sec-head--split">
                <div>
                  <span className="t-eyebrow">Services</span>
                  <h2 className="t-h2" style={{ marginTop: 14 }}>
                    One app for everything
                    <br />
                    you need moved.
                  </h2>
                </div>
                <p className="t-lead sec-head__lead">
                  Whatever you need delivered or picked up — or wherever you need
                  to go — PABS has a dedicated flow for it: clear steps, live
                  tracking, and fair pricing.
                </p>
              </div>
            </Reveal>

            <div className="rail svc-rail svc-rail--4">
              {SERVICES.map((s, i) => (
                  <Reveal key={s.key} delay={i * 90}>
                    <article className={`card svc svc--${s.tone} card--hover`}>
                      {/* Icon and label read as one cluster: the tag sits directly
                          above the title it qualifies, rather than floating off in
                          the opposite corner of the card. */}
                      <div className="svc__head">
                        <span className={`icon-tile icon-tile--${s.tone}`}>{s.icon}</span>
                        <div className="svc__head-text">
                          <span className="svc__tag">{s.tag}</span>
                          <h3 className="card__title">{s.title}</h3>
                        </div>
                      </div>
                      <p className="card__body">{s.body}</p>
                      <ul className="svc__points">
                        {s.points.map((p) => (
                          <li key={p}>
                            <span className="svc__bullet" aria-hidden="true" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </article>
                  </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ================= How it works (ink) ================= */}
        <section className="u-section u-section--ink how">
          <div className="u-container">
            <Reveal>
              <div className="sec-head sec-head--center">
                <span className="t-eyebrow">How it works</span>
                <h2 className="t-h2">From tap to doorstep</h2>
                <p className="t-lead">
                  Every PABS request follows the same three beats, whichever
                  service you pick.
                </p>
              </div>
            </Reveal>

            <ol className="how__steps">
              {STEPS.map((step, i) => (
                <Reveal as="li" key={step.n} delay={i * 110} className="how__step">
                  <span className="how__n">{step.n}</span>
                  <h3 className="t-h4 how__title">{step.title}</h3>
                  <p className="how__body">{step.body}</p>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* ================= Pricing ================= */}
        {SHOW_PRICING ? (
        <section className="u-section" id="pricing">
          <div className="u-container">
            <div className="pricing">
              <Reveal className="pricing__aside">
                <span className="t-eyebrow">Pricing</span>
                <h2 className="t-h2" style={{ marginTop: 14 }}>
                  Transparent
                  <br />
                  Pasugo fares.
                </h2>
                <p className="t-lead" style={{ marginTop: 18 }}>
                  You'll see the fare estimate in the app before you confirm.
                  Final totals may adjust based on actual distance. The same
                  fare applies to everyone — no haggling, and no surcharges.
                </p>
                {/* Stated exactly as the Rider Agreement §6.2 scopes it: the promo
                    runs from each service's own launch, not from a fixed date. */}
                <p className="pricing__promo">
                  <strong>Launch promo:</strong> ₱13 per kilometer for the first
                  two months from the launch of each service.
                </p>
                <a className="link-arrow pricing__link" href="/faq#fares-and-fees">
                  How fares are calculated
                  <IconArrowRight size={16} />
                </a>
              </Reveal>

              <div className="pricing__grid">
                {PRICING.map((p, i) => (
                  <Reveal key={p.label} delay={i * 80}>
                    <div className="fare">
                      <span className="fare__icon">{p.icon}</span>
                      <div className="fare__label">{p.label}</div>
                      <div className="fare__amount">
                        {p.amount}
                        {p.unit ? <span className="fare__unit">{p.unit}</span> : null}
                      </div>
                      <p className="fare__desc">{p.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>
        ) : null}

        {/* ================= Payments ================= */}
        <section className="u-section u-section--soft" id="payments">
          <div className="u-container">
            <Reveal>
              <div className="sec-head sec-head--split">
                <div>
                  <span className="t-eyebrow">Payments</span>
                  <h2 className="t-h2" style={{ marginTop: 14 }}>
                    Pay cash on delivery,
                    <br />
                    or with GCash.
                  </h2>
                </div>
                <p className="t-lead sec-head__lead">
                  Choose at checkout, for every service. No cards, and we never
                  ask for your bank or GCash login.
                </p>
              </div>
            </Reveal>

            <div className="grid grid--3 pay__grid">
              {PAYMENTS.map((p, i) => (
                <Reveal key={p.title} delay={i * 70}>
                  <article className="card card--hover card--flat pay">
                    <h3 className="pay__title">{p.title}</h3>
                    <p className="card__body">{p.body}</p>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal delay={120}>
              <p className="pay__note">
                <IconWallet size={18} />
                <span>
                  For store orders, the rider pays the store and keeps the
                  receipt, and a photo of it appears in your booking. Paying cash?
                  Settle the total with your rider on delivery.
                </span>
              </p>
            </Reveal>
          </div>
        </section>

        {/* ================= Join PABS ================= */}
        <section className="u-section" id="join">
          <div className="u-container">
            <Reveal>
              <div className="sec-head sec-head--center">
                <span className="t-eyebrow">Join PABS</span>
                <h2 className="t-h2" style={{ marginTop: 14 }}>
                  Grow with us.
                </h2>
                <p className="t-lead">
                  Bring your store to PABS, or earn as a rider. Both start with a
                  short form.
                </p>
              </div>
            </Reveal>

            <div className="join__grid">
              <Reveal>
                <a className="card card--hover join__card" href="/merchants">
                  <span className="icon-tile icon-tile--amber">
                    <IconStore size={22} />
                  </span>
                  <h3 className="card__title">Sell on PABS</h3>
                  <p className="card__body">
                    List your restaurant, store, or pharmacy. Riders pay for
                    each order at pickup.
                  </p>
                  <span className="link-arrow">
                    Sign up your store
                    <IconArrowRight size={16} />
                  </span>
                </a>
              </Reveal>
              <Reveal delay={90}>
                <a className="card card--hover join__card" href="/riders">
                  <span className="icon-tile icon-tile--violet">
                    <IconErrand size={22} />
                  </span>
                  <h3 className="card__title">Become a rider</h3>
                  <p className="card__body">
                    Deliver, run errands, and give Angkas rides as an independent
                    partner. Leave your name and number and we'll call you.
                  </p>
                  <span className="link-arrow">
                    Apply to ride
                    <IconArrowRight size={16} />
                  </span>
                </a>
              </Reveal>
            </div>

            <Reveal delay={120}>
              <p className="join__jobs">
                Looking for other roles? <a href="/jobs">See open jobs</a>.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ================= Merchants ================= */}
        {SHOW_MERCHANT_PLANS ? (
        <section className="u-section" id="merchants">
          <div className="u-container">
            <Reveal>
              <div className="sec-head">
                <span className="t-eyebrow">For merchants</span>
                <h2 className="t-h2" style={{ marginTop: 14 }}>
                  Run a restaurant, grocery,
                  <br />
                  or pharmacy?
                </h2>
                <p className="t-lead">
                  Reach customers across food, grocery, and pharmacy categories —
                  from one merchant dashboard.
                </p>
              </div>
            </Reveal>

            {/* Plans get visual weight; supporting details sit beneath them */}
            <div className="plans">
              <Reveal>
                <article className="card card--hover plan">
                  <span className="badge badge--neutral">Free</span>
                  <h3 className="plan__name">Starter</h3>
                  <p className="card__body">
                    List your store at no cost. A small service fee applies to
                    customer orders.
                  </p>
                  <a className="btn btn--secondary btn--block plan__cta" href="/contact">
                    Get started
                  </a>
                </article>
              </Reveal>

              <Reveal delay={90}>
                <article className="card card--hover plan plan--featured">
                  <span className="badge badge--solid">30-day trial</span>
                  <h3 className="plan__name">Business</h3>
                  <p className="card__body">
                    Analytics, Partner and Sponsored badges, full order history,
                    marketing tools, and no customer-facing service fee.
                  </p>
                  <a className="btn btn--primary btn--block plan__cta" href="/contact">
                    Contact sales
                    <IconArrowRight size={17} className="btn__icon btn__icon--arrow" />
                  </a>
                </article>
              </Reveal>

              <Reveal delay={180} className="plan__extras">
                <div className="card card--soft card--flat mfeat">
                  <span className="icon-tile icon-tile--violet mfeat__icon">
                    <IconChart size={20} />
                  </span>
                  <div>
                    <h4 className="t-h4">Flexible commission</h4>
                    <p className="card__body">
                      Add a small tip on top of your prices, or have it deducted
                      from your merchant wallet after each sale.
                    </p>
                  </div>
                </div>
                <div className="card card--soft card--flat mfeat">
                  <span className="icon-tile icon-tile--violet mfeat__icon">
                    <IconStore size={20} />
                  </span>
                  <div>
                    <h4 className="t-h4">Wallet billing</h4>
                    <p className="card__body">
                      Monthly Business billing runs from your merchant wallet.
                      Free during early access.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={120}>
              <div className="btn-row merch__ctas">
                <a className="btn btn--primary" href="/contact">
                  Contact sales
                </a>
                <a className="btn btn--secondary" href="/faq#merchant-join">
                  <IconChat size={17} />
                  Merchant FAQs
                </a>
              </div>
            </Reveal>
          </div>
        </section>
        ) : null}
      </main>
    </>
  );
};

export default HomePage;
